import { BufferAttribute, BufferGeometry, DynamicDrawUsage, Points, ShaderMaterial } from "three";
import type { Catalogue } from "../../data/schema";
import type { FrameData } from "../frame";
import type { Tier } from "./tier";
import { colorAttribute } from "./colors";

const vertex = /* glsl */ `
  #include <common>
  #include <logdepthbuf_pars_vertex>
  attribute float aRadius;
  attribute float aVisible;
  varying float vVisible;
  attribute vec3 aColor;
  uniform float uPixelsPerUnit;
  uniform float uPixelRatio;
  varying vec3 vColor;
  void main() {
    vVisible = aVisible;
    vColor = aColor;
    vec4 mv = modelViewMatrix * vec4(position, 1.0);
    gl_Position = projectionMatrix * mv;
    float px = aRadius * uPixelsPerUnit / max(-mv.z, 1e-9);
    // clamp in CSS pixels, then convert: gl_PointSize is in drawing-buffer pixels
    gl_PointSize = clamp(px, 1.5, 4.0) * uPixelRatio;
    #include <logdepthbuf_vertex>
  }`;

const fragment = /* glsl */ `
  #include <common>
  #include <logdepthbuf_pars_fragment>
  varying vec3 vColor;
  varying float vVisible;
  void main() {
    if (vVisible < 0.5) discard;
    vec2 d = gl_PointCoord - 0.5;
    if (dot(d, d) > 0.25) discard;
    gl_FragColor = vec4(vColor, 1.0);
    #include <tonemapping_fragment>
    #include <colorspace_fragment>
    #include <logdepthbuf_fragment>
  }`;

export class PointsTier implements Tier {
  readonly object: Points;
  private readonly position: BufferAttribute;
  private readonly radius: BufferAttribute;
  private readonly visible: BufferAttribute;
  private readonly material: ShaderMaterial;

  constructor(
    bodies: Catalogue,
    private readonly indices: number[],
  ) {
    const geometry = new BufferGeometry();
    this.position = new BufferAttribute(new Float32Array(3 * indices.length), 3).setUsage(
      DynamicDrawUsage,
    );
    this.radius = new BufferAttribute(new Float32Array(indices.length), 1).setUsage(
      DynamicDrawUsage,
    );
    this.visible = new BufferAttribute(new Float32Array(indices.length), 1).setUsage(
      DynamicDrawUsage,
    );
    geometry.setAttribute("aVisible", this.visible);
    geometry.setAttribute("position", this.position);
    geometry.setAttribute("aRadius", this.radius);
    geometry.setAttribute("aColor", new BufferAttribute(colorAttribute(bodies, indices), 3));
    this.material = new ShaderMaterial({
      vertexShader: vertex,
      fragmentShader: fragment,
      uniforms: { uPixelsPerUnit: { value: 1 }, uPixelRatio: { value: 1 } },
    });
    this.object = new Points(geometry, this.material);
    this.object.frustumCulled = false;
  }

  update(frame: FrameData): void {
    const { positions, radii, viewportHeight, fovRad, pixelRatio } = frame;
    const pos = this.position.array as Float32Array,
      rad = this.radius.array as Float32Array;
    this.indices.forEach((index, k) => {
      const o = 3 * index;
      pos[3 * k] = positions[o];
      pos[3 * k + 1] = positions[o + 1];
      pos[3 * k + 2] = positions[o + 2];
      rad[k] = radii[index];
      this.visible.setX(k, frame.visible[index]);
    });
    this.position.needsUpdate = true;
    this.radius.needsUpdate = true;
    this.visible.needsUpdate = true;
    this.material.uniforms.uPixelsPerUnit.value = viewportHeight / 2 / Math.tan(fovRad / 2);
    this.material.uniforms.uPixelRatio.value = pixelRatio;
  }
}
